import { useState } from 'react';
import { useAuthorizeCompanionMutation, type Me } from './api.js';
import { Logo } from './brand.js';
import { Alert, Button } from './ui.js';

/**
 * Connecting the ContactPilot Browser Companion to this person in this workspace.
 *
 * Reached only from the extension (through Chrome's sign-in window), with the extension's
 * own redirect address and a PKCE challenge. Nothing is handed back until the person
 * chooses Connect; the server refuses any extension it does not recognise. The result is
 * a one-time code the extension exchanges for its own revocable credential — this page
 * never gives a browser session, a password or a CRM credential to anything.
 */
export function CompanionConnect({ me, workspaceName }: { me: Me; workspaceName: string | null }) {
  const params = new URLSearchParams(window.location.search);
  const redirectUri = params.get('redirect_uri') ?? '';
  const challenge = params.get('code_challenge') ?? '';
  const state = params.get('state') ?? '';
  const [authorize, { isLoading }] = useAuthorizeCompanionMutation();
  const [error, setError] = useState<string | null>(null);
  const valid = /^https:\/\/[a-p]{32}\.chromiumapp\.org\/?$/.test(redirectUri) && /^[A-Za-z0-9_-]{43,128}$/.test(challenge) && state.length > 0;

  const back = (q: Record<string, string>) => {
    const u = new URL(redirectUri);
    for (const [k, v] of Object.entries({ ...q, state })) u.searchParams.set(k, v);
    window.location.assign(u.toString());
  };

  return (
    <main className="grid min-h-screen place-items-center bg-gradient-to-br from-cp-sky via-cp-bg to-cp-mint p-4">
      <div className="w-full max-w-md space-y-4 rounded-card border border-cp-border bg-white p-6 shadow-lift">
        <Logo />
        <h1 className="text-xl font-bold text-slate-900">Connect the Browser Companion</h1>
        {!valid ? (
          <Alert tone="error">This connection request is incomplete. Start again from the ContactPilot extension.</Alert>
        ) : (
          <>
            <p className="text-sm text-slate-700">
              The extension will save contacts you capture into <strong>{workspaceName ?? 'your workspace'}</strong> as <strong>{me.email ?? 'you'}</strong>.
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
              <li>It reads a page only when you click it, and sends only the section you confirm.</li>
              <li>Saved contacts wait in Review. Nothing reaches your CRM until you approve it.</li>
              <li>It cannot read your mailbox, your CRM or your settings.</li>
              <li>You can disconnect it at any time in Settings → Integrations.</li>
            </ul>
            {error ? <Alert tone="error">{error}</Alert> : null}
            <div className="flex flex-wrap gap-2">
              <Button disabled={isLoading} onClick={async () => {
                setError(null);
                const r = await authorize({ redirectUri, codeChallenge: challenge });
                if ('data' in r && r.data) back({ code: r.data.code });
                else setError((r.error as { data?: { message?: string } }).data?.message ?? 'The extension could not be connected.');
              }}>Connect</Button>
              <Button variant="secondary" onClick={() => back({ error: 'access_denied' })}>Cancel</Button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
